"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { UserRole } from "@/lib/auth";

interface BottomNavProps {
  userRole: UserRole;
  unreadCount: number;
}

export function BottomNav({ userRole, unreadCount }: BottomNavProps) {
  const pathname = usePathname();

  if (!userRole || pathname === "/") return null;

  const items =
    userRole === "tradesperson"
      ? [
          { href: "/", label: "Home", icon: HomeIcon },
          { href: "/jobs", label: "Jobs", icon: JobsIcon },
          {
            href: "/dashboard/messages",
            label: "Messages",
            icon: MessagesIcon,
            badge: unreadCount,
          },
          { href: "/dashboard", label: "Dashboard", icon: DashboardIcon },
        ]
      : [
          { href: "/", label: "Home", icon: HomeIcon },
          { href: "/browse", label: "Browse", icon: BrowseIcon },
          {
            href: "/homeowner/messages",
            label: "Messages",
            icon: MessagesIcon,
            badge: unreadCount,
          },
          {
            href: "/homeowner/dashboard",
            label: "Dashboard",
            icon: DashboardIcon,
          },
        ];

  return (
    <nav
      className="app-bottom-nav fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-[#0f172a] border-t border-stone dark:border-slate-700 sm:hidden"
      aria-label="Mobile navigation"
    >
      <ul className="flex items-center justify-around h-14 px-2">
        {items.map((item) => {
          const isActive =
            item.href === "/"
              ? pathname === "/"
              : pathname === item.href ||
                pathname.startsWith(item.href + "/");
          return (
            <li key={item.href}>
              <Link
                href={item.href}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[44px] rounded-sm px-2 py-1 no-underline transition-colors ${
                  isActive
                    ? "text-forest"
                    : "text-text-muted hover:text-forest"
                }`}
              >
                <span className="relative">
                  <item.icon active={isActive} />
                  {item.badge && item.badge > 0 ? (
                    <span className="absolute -top-1 -right-2 min-w-[16px] h-4 flex items-center justify-center bg-copper text-white text-[10px] font-bold rounded-full px-1">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  ) : null}
                </span>
                <span
                  className={`text-[10px] leading-tight ${isActive ? "font-semibold" : "font-medium"}`}
                >
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
      {!active && <path d="M9 21V12h6v9" />}
    </svg>
  );
}

function JobsIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path
        d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
      />
    </svg>
  );
}

function BrowseIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.25 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function MessagesIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" />
    </svg>
  );
}

function DashboardIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}
